import { arrayToObject, groupBy, nonNullable } from 'lib/arrayUtils'
import cn from 'clsx'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import HotelMonthTile from './OfferCalendarHotelMonthTile'
import FlightsMonthTile from './OfferCalendarFlightsMonthTile'
import { CalendarPickerMonthView, getCalendarDays, getTacticalScheduleMap } from 'selectors/offerPageSelectors'
import { getCalendarMonths, getMonthsOtherAvailability, getSelectedOfferPageDay, isAnyFlightsActive } from 'selectors/multipleRoomBookingSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'
import { calendarDateToMoment } from 'lib/time'
import Heading from 'components/Luxkit/Typography/Heading'
import Clickable from 'components/Common/Clickable'
import OfferCalendarPriceBlurb from '../OfferCalendarPriceBlurb'
import TextLoadingBox from 'components/Luxkit/Typography/TextLoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import SolidUsdCircleIcon from 'components/Luxkit/Icons/solid/SolidUsdCircleIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LineFireIcon from 'components/Luxkit/Icons/line/LineFireIcon'
import Group from 'components/utils/Group'
import useBundleSelectedDays from 'components/OfferPage/hooks/useBundleSelectedDays'

const Root = styled(VerticalSpacer)`
  padding-bottom: ${rem(32)};
`

const YearWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${rem(12)};

  ${mediaQueryUp.sm} {
    grid-gap: ${rem(20)};
    grid-template-columns: repeat(4, 1fr);
  }
`

const MonthTile = styled(Clickable)`
  display: flex;
  flex-direction: column;
  justify-content: start;
  position: relative;
  text-align: left;
  overflow: hidden;
  border: 1px solid ${props => props.theme.palette.neutral.five};
  background-color: ${props => props.theme.palette.neutral.eight};
  box-shadow: none;
  transition: background-color 0.2s, box-shadow 0.2s;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${rem(12)};
  min-height: ${rem(70)};

  > * + * {
    margin-top: ${rem(4)};
  }

  &:hover, &:focus {
    &:not(:disabled) {
      box-shadow: ${props => props.theme.shadow.bottom.medium};
    }
  }

  &.sold-out {
    background-color: ${props => props.theme.palette.neutral.seven};
  }

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normal};
    color: ${props => props.theme.palette.neutral.one};
  }

`

interface Props {
  className?: string;
  monthViews: Array<CalendarPickerMonthView>;
  daysByMonth: ReturnType<typeof getCalendarDays>;
  isTacticalRate: ReturnType<typeof getTacticalScheduleMap>;
  enableCalendarRecommendations: boolean;
  monthAvailability: { [month: string]: boolean };
  onMonthSelect?: (monthKey: string) => void;
  duration: number;
  room?: App.PackageRoomType;
  flightsOnly?: boolean;
  selectedDay?: App.CalendarDay;
  isAnyFlightsActive: boolean;
  bundleOfferId?: string;
}

function OfferCalendarMonthPicker(props: Props) {
  const {
    className,
    enableCalendarRecommendations,
    monthViews,
    onMonthSelect,
    isTacticalRate,
    duration,
    monthAvailability,
    room,
    flightsOnly,
    selectedDay,
    isAnyFlightsActive,
    bundleOfferId,
    daysByMonth,
  } = props

  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onMonthClick = useCallback((month: CalendarPickerMonthView) => {
    const hasOtherAvailability = room && !!monthAvailability[month.key]
    const shouldShowRecommendations = flightsOnly && !month.packagedAvailable && !hasOtherAvailability && enableCalendarRecommendations
    if (shouldShowRecommendations) {
      dispatch({ type: OfferPageActions.showRecommendationsView, monthIndex: month.key })
    } else {
      if (isAnyFlightsActive && !month.packagedAvailable) {
        if (enableCalendarRecommendations && !month.hotelAvailable) {
          dispatch({ type: OfferPageActions.showRecommendationsView, monthIndex: month.key })
        } else {
          dispatch({ type: OfferPageActions.setFlights, flights: false })
        }
      } else if (month.hotelAvailable) {
        onMonthSelect?.(month.key)
      } else if (room && monthAvailability[month.key]) {
        dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'room' })
      } else if (enableCalendarRecommendations) {
        dispatch({ type: OfferPageActions.showRecommendationsView, monthIndex: month.key })
      }
    }
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.MonthSelect, calendarDateToMoment(month).format(ISO_DATE_SHORT_FORMAT))
  }, [
    dispatch,
    flightsOnly,
    isAnyFlightsActive,
    monthAvailability,
    onMonthSelect,
    onUserAnalyticsAction,
    room,
    enableCalendarRecommendations,
  ])

  const monthsByYear = useMemo(() => groupBy(monthViews, view => view.year), [monthViews])

  const [monthByPurchasableTactical, yearByPurchasableTactical] = useMemo(() => {
    const byYear = new Map<string, boolean>()
    const byMonth = new Map<string, boolean>()

    for (const [year, months] of monthsByYear.entries()) {
      for (const month of months) {
        const key = `${month.month} ${year}`
        const entry = daysByMonth.find(entry => entry[0] === key)
        const days = nonNullable(entry?.[1] ?? [])
        const monthHasPurchasableTactical = days.some(day => isTacticalRate(day.roomRateId, day.checkIn))
        byMonth.set(key, monthHasPurchasableTactical)
      }
      byYear.set(year, Array.from(byMonth.values()).some(Boolean))
    }

    return [byMonth, byYear]
  }, [monthsByYear, daysByMonth, isTacticalRate])

  const yearPrices = useMemo(() => {
    return arrayToObject(monthsByYear.entries(),
      ([year]) => year,
      ([_, months]) => {
        const withTotals = months.filter(month => month.hotelAvailable)
        const hotelFromPrice = Math.min(...withTotals.map(m => m.hotelTotal))
        const packagedFromPrice = Math.min(...withTotals.map(m => m.packagedTotal))

        return {
          hotelFromPrice,
          packagedFromPrice,
        } })
  }, [monthsByYear])

  const bundleSelectedDays = useBundleSelectedDays()

  return (
    <Root className={className} gap={24}>
      {Array.from(monthsByYear.entries()).map(([year, months]) => {
        const fromPrice = isAnyFlightsActive ? yearPrices[year].packagedFromPrice : yearPrices[year].hotelFromPrice
        const yearHasPurchasableTactical = yearByPurchasableTactical.get(year) ?? false

        return <VerticalSpacer key={year} gap={12}>
          <Group direction="vertical" smDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={4}>
            <Group direction="vertical" fullWidth>
              <Heading variant="heading3">{year}</Heading>
              {months.some(month => month.loading) && <TextLoadingBox typography="body-medium" width={280} />}
              {Number.isFinite(fromPrice) && fromPrice > 0 && !bundleOfferId && <OfferCalendarPriceBlurb year={year} fromPrice={fromPrice} />}
            </Group>
            {yearHasPurchasableTactical && <BodyTextBlock startIcon={<LineFireIcon colour="secondary-normal" />} variant="medium">
              Bonus Inclusions Available
            </BodyTextBlock>}
          </Group>
          <YearWrapper>
            {months.map((month) => {
              const available = flightsOnly ? month.packagedAvailable : month.hotelAvailable
              const hasOtherAvailability = room && !!monthAvailability[month.key] && !bundleOfferId
              const disabled = (!available && !hasOtherAvailability && !enableCalendarRecommendations) || month.loading
              const selected = selectedDay?.monthKey === month.key
              const key = `${month.month} ${year}`
              const monthHasPurchasableTactical = monthByPurchasableTactical.get(key) ?? false

              return <MonthTile
                className={cn({ 'sold-out': !available, selected })}
                onClick={() => onMonthClick(month)}
                key={month.month}
                disabled={disabled}
                data-testid={cn({
                  'month-tile': month.hotelAvailable,
                  'month-tile-with-flights': month.packagedAvailable,
                })}
              >
                {month.limitedAvailability && <UrgencyLabel variant="critical1">Limited availability</UrgencyLabel>}
                <Group fullWidth direction="horizontal" horizontalAlign="space-between">
                  <Heading variant="heading6">{month.month}</Heading>
                  {month.hasPromo && <SolidUsdCircleIcon size="S" colour={selectedDay?.monthKey !== month.key ? 'secondary-normal' : undefined} />}
                  {!month.hasPromo && monthHasPurchasableTactical && <LineFireIcon colour="secondary-normal" />}
                </Group>
                {month.loading && <VerticalSpacer gap={4}>
                  <TextLoadingBox typography="caption-large" width="100%" />
                  <TextLoadingBox typography="body-large" width="100%" />
                </VerticalSpacer>}
                {!month.loading && <>
                  {isAnyFlightsActive && <FlightsMonthTile
                    flightsOnly={flightsOnly}
                    month={month}
                    duration={bundleOfferId ? bundleSelectedDays.duration : duration}
                    selected={selected}
                    enableCalendarRecommendations={enableCalendarRecommendations}
                    hasPromo={month.hasPromo}
                  />}
                  {!isAnyFlightsActive && <HotelMonthTile
                    month={month}
                    duration={bundleOfferId ? bundleSelectedDays.duration : duration}
                    selected={selected}
                    hasOtherAvailability={!!hasOtherAvailability}
                    enableCalendarRecommendations={enableCalendarRecommendations}
                  />}
                </>
                }
              </MonthTile>
            })}
          </YearWrapper>
        </VerticalSpacer>
      })}
    </Root>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    bundleOfferId: state.bundleOffer?.id,
    airport: state.airport,
    duration: currentState.duration,
    enableCalendarRecommendations: !state.bundleOffer ? shouldEnableCalendarRecommendations(appState, state.offer) : false,
    flights: state.flights,
    flightsOnly: state.offer.bundledWithFlightsOnly,
    monthAvailability: getMonthsOtherAvailability(state, appState),
    monthViews: getCalendarMonths(currentState, appState).months,
    daysByMonth: getCalendarDays(appState, state),
    isTacticalRate: getTacticalScheduleMap(state),
    room: state.room,
    selectedDay: getSelectedOfferPageDay(state, appState),
    isAnyFlightsActive: isAnyFlightsActive(state, appState),
  }
})(OfferCalendarMonthPicker)
